import { useState, useCallback } from "react"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { GradientSparkleIcon } from "@/components/extraction/GradientSparkleIcon"
import type { AnalysisCategory } from "@/types/extraction"

export interface AnalysisItem {
  key: "tender_summary" | AnalysisCategory
  label: string
  description: string
  mandatory: boolean
}

interface AnalyzeSelectionDialogProps {
  trigger: React.ReactNode
  items: AnalysisItem[]
  onConfirm: (selectedKeys: AnalysisItem["key"][]) => void
  disabled?: boolean
}

export function AnalyzeSelectionDialog({
  trigger,
  items,
  onConfirm,
  disabled,
}: AnalyzeSelectionDialogProps) {
  const [open, setOpen] = useState(false)
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set())

  // Select everything on OPEN, and only on open.
  //
  // This used to be an effect keyed on `[open, items]`. `items` is rebuilt by the
  // parent on every render, so any background refresh while the dialog was open
  // handed it a fresh array identity and silently re-ticked every box the user
  // had deliberately unticked — and they were billed for the categories they had
  // just declined. Every open funnels through here (the trigger calls
  // `onOpenChange`), so initialising at the transition covers all entry points
  // without depending on prop identity.
  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      if (disabled && newOpen) return
      if (newOpen) setSelectedKeys(new Set(items.map((item) => item.key)))
      setOpen(newOpen)
    },
    [disabled, items]
  )

  const handleToggle = useCallback(
    (key: string, checked: boolean) => {
      setSelectedKeys((prev) => {
        const next = new Set(prev)
        if (checked) {
          next.add(key)
        } else {
          next.delete(key)
        }
        return next
      })
    },
    []
  )

  const handleConfirm = useCallback(() => {
    onConfirm(Array.from(selectedKeys) as AnalysisItem["key"][])
    setOpen(false)
  }, [onConfirm, selectedKeys])

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Run Bid Analysis</DialogTitle>
          <DialogDescription>
            Select which analyses to run on your tender documents.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-1 py-2">
          {items.map((item) => (
            <label
              key={item.key}
              className="flex items-start gap-3 rounded-lg px-2 py-2.5 hover:bg-muted/50 transition-colors cursor-pointer"
            >
              <Checkbox
                checked={selectedKeys.has(item.key)}
                onCheckedChange={(checked) =>
                  handleToggle(item.key, checked === true)
                }
                disabled={item.mandatory}
                className="mt-0.5"
              />
              <div className="grid gap-0.5">
                <span className="text-sm font-medium leading-none">
                  {item.label}
                  {item.mandatory && (
                    <>
                      {' '}
                      <span className="ml-2 text-[10px] font-medium text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                        Required
                      </span>
                    </>
                  )}
                </span>
                <span className="text-meta">
                  {item.description}
                </span>
              </div>
            </label>
          ))}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={selectedKeys.size === 0} className="gap-2">
            <GradientSparkleIcon className="h-4 w-4" />
            Run Analysis
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
